import { useEffect, useMemo, useState } from 'react';

type TabKey = 'team' | 'conversation' | 'requirement' | 'shortlists';

type ApiConsultant = {
  id: string;
  fullName: string;
  role: string;
  category: string;
  seniority: string;
  primaryStack: string | string[] | null;
  lastConversation?: {
    happenedAt?: string;
    summary?: string;
  } | null;
  intelFreshness?: 'fresh' | 'stale' | 'very-stale' | 'none';
};

type ScoreBreakdown = {
  technical: number;
  freshness: number;
  interest: number;
  availability: number;
  clientContext: number;
  responsiveness: number;
};

type InterviewGuide = {
  id: string;
  openingScript: string;
  keyQuestions: string[];
  casesFromCv: Array<{
    caseTitle: string;
    whyRelevant: string;
    drillDownQuestions: string[];
  }>;
  redFlags: string[];
  decisionCriteria: string;
};

type ShortlistCandidate = {
  consultant: ApiConsultant;
  interviewPriority: number;
  scoreBreakdown: ScoreBreakdown;
  lastConversation: {
    happenedAt: string;
    summary: string;
  } | null;
  risks: string[];
  interviewGuide: InterviewGuide;
};

type ShortlistResult = {
  candidates: ShortlistCandidate[];
  totalEvaluated: number;
  vetoedCount: number;
  staleIntelCount: number;
  durationMs: number;
};

const tabs: Array<{ key: TabKey; label: string }> = [
  { key: 'team', label: 'Mi equipo' },
  { key: 'conversation', label: 'Registrar conversación' },
  { key: 'requirement', label: 'Nuevo requerimiento' },
  { key: 'shortlists', label: 'Shortlists activos' },
];

function parseStack(value: string | string[] | null | undefined): string[] {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

function freshnessClass(freshness: ApiConsultant['intelFreshness']): string {
  if (freshness === 'fresh') return 'bg-emerald-100 text-emerald-800';
  if (freshness === 'stale') return 'bg-amber-100 text-amber-800';
  if (freshness === 'very-stale') return 'bg-rose-100 text-rose-800';
  return 'bg-slate-100 text-slate-700';
}

function scoreColor(score: number): string {
  if (score >= 80) return 'text-emerald-400';
  if (score >= 60) return 'text-cyan-400';
  if (score >= 40) return 'text-amber-400';
  return 'text-rose-400';
}

export default function App() {
  const [activeTab, setActiveTab] = useState<TabKey>('team');
  const [consultants, setConsultants] = useState<ApiConsultant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Shortlist state
  const [shortlistLoading, setShortlistLoading] = useState(false);
  const [shortlistResult, setShortlistResult] = useState<ShortlistResult | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<ShortlistCandidate | null>(null);
  
  // Form states
  const [reqForm, setReqForm] = useState({
    clientName: '',
    requiredStack: '',
    minSeniority: 'mid',
    rateMax: '',
    startDate: '',
    freeText: '',
  });
  
  const [convForm, setConvForm] = useState({
    consultantId: '',
    summary: '',
    declaredInterests: '',
    declaredAvoids: '',
    declaredAvailability: '',
    channel: 'whatsapp',
  });
  
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetch('/api/consultants', { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json() as Promise<{ data?: ApiConsultant[] }>;
      })
      .then((payload) => {
        setConsultants(payload.data ?? []);
      })
      .catch((fetchError: unknown) => {
        if ((fetchError as Error).name === 'AbortError') return;
        setError('No se pudo cargar el equipo.');
      })
      .finally(() => {
        setLoading(false);
      });

    return () => controller.abort();
  }, []);

  const topStacks = useMemo(
    () => consultants.map((consultant) => ({ id: consultant.id, stack: parseStack(consultant.primaryStack).slice(0, 3) })),
    [consultants],
  );
  
  const handleCreateRequirement = async (e: React.FormEvent) => {
    e.preventDefault();
    setShortlistLoading(true);
    setError(null);
    setFormSuccess(null);
    
    try {
      // First create the requirement
      const reqPayload = {
        clientName: reqForm.clientName,
        requiredStack: reqForm.requiredStack.split(',').map(s => s.trim()).filter(Boolean),
        minSeniority: reqForm.minSeniority,
        rateMax: reqForm.rateMax ? parseInt(reqForm.rateMax) : null,
        startDate: reqForm.startDate || new Date().toISOString().split('T')[0],
        freeText: reqForm.freeText,
      };
      
      const reqResponse = await fetch('/api/requirements', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reqPayload),
      });
      
      if (!reqResponse.ok) {
        throw new Error('Error al crear requerimiento');
      }
      
      // Then generate shortlist
      const shortlistResponse = await fetch('/api/shortlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reqPayload),
      });
      
      if (!shortlistResponse.ok) {
        throw new Error('Error al generar shortlist');
      }
      
      const result = await shortlistResponse.json() as { data: ShortlistResult };
      setShortlistResult(result.data);
      setActiveTab('shortlists');
      setFormSuccess('Shortlist generado exitosamente');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setShortlistLoading(false);
    }
  };
  
  const handleSaveConversation = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setFormSuccess(null);
    
    try {
      const payload = {
        happenedAt: new Date().toISOString(),
        channel: convForm.channel,
        summary: convForm.summary,
        declaredInterests: convForm.declaredInterests.split(',').map(s => s.trim()).filter(Boolean),
        declaredAvoids: convForm.declaredAvoids.split(',').map(s => s.trim()).filter(Boolean),
        declaredAvailability: convForm.declaredAvailability,
      };
      
      const response = await fetch(`/api/consultants/${convForm.consultantId}/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        throw new Error('Error al guardar conversación');
      }
      
      setFormSuccess('Conversación guardada exitosamente');
      setConvForm({
        consultantId: '',
        summary: '',
        declaredInterests: '',
        declaredAvoids: '',
        declaredAvailability: '',
        channel: 'whatsapp',
      });
      
      // Refresh consultants list
      const consultantsRes = await fetch('/api/consultants');
      const consultantsData = await consultantsRes.json() as { data?: ApiConsultant[] };
      setConsultants(consultantsData.data ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto flex min-h-screen max-w-7xl flex-col px-4 py-6 sm:px-6 lg:px-8">
        <header className="mb-6 flex flex-col gap-3 border-b border-slate-800 pb-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-cyan-300">Interview Intel</p>
            <h1 className="mt-2 text-3xl font-semibold">Sistema de staffing para consultoría técnica LATAM</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-400">
              Prioriza entrevistas internas con base en stack, frescura de intel, interés declarado y disponibilidad.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/80 px-4 py-3 text-sm text-slate-300">
            <div>Total consultores visibles: {consultants.length}</div>
            <div>URL local: http://localhost:5173</div>
          </div>
        </header>

        <nav className="mb-6 flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={
                activeTab === tab.key
                  ? 'rounded-full bg-cyan-400 px-4 py-2 text-sm font-medium text-slate-950'
                  : 'rounded-full border border-slate-700 bg-slate-900 px-4 py-2 text-sm text-slate-300 hover:border-slate-500'
              }
            >
              {tab.label}
            </button>
          ))}
        </nav>
        
        {formSuccess && (
          <div className="mb-4 rounded-2xl border border-emerald-800 bg-emerald-950/50 px-4 py-3 text-emerald-300">
            {formSuccess}
          </div>
        )}
        
        {error && (
          <div className="mb-4 rounded-2xl border border-rose-800 bg-rose-950/50 px-4 py-3 text-rose-300">
            {error}
          </div>
        )}

        {activeTab === 'team' ? (
          <section className="rounded-3xl border border-slate-800 bg-slate-900/70 p-5 shadow-2xl shadow-black/30">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold">Mi equipo</h2>
              <span className="text-sm text-slate-400">Última conversación + badge de freshness</span>
            </div>

            {loading ? <p className="text-sm text-slate-400">Cargando consultores...</p> : null}

            {!loading && !error ? (
              <div className="overflow-hidden rounded-2xl border border-slate-800">
                <table className="min-w-full divide-y divide-slate-800 text-sm">
                  <thead className="bg-slate-950/70 text-left text-slate-400">
                    <tr>
                      <th className="px-4 py-3 font-medium">Nombre</th>
                      <th className="px-4 py-3 font-medium">Rol</th>
                      <th className="px-4 py-3 font-medium">Stack top-3</th>
                      <th className="px-4 py-3 font-medium">Última conversación</th>
                      <th className="px-4 py-3 font-medium">Freshness</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800 bg-slate-900/50">
                    {consultants.map((consultant) => {
                      const stack = topStacks.find((item) => item.id === consultant.id)?.stack ?? [];
                      return (
                        <tr key={consultant.id} className="align-top">
                          <td className="px-4 py-4">
                            <div className="font-medium text-slate-100">{consultant.fullName}</div>
                            <div className="text-xs text-slate-500">{consultant.seniority} · {consultant.category}</div>
                          </td>
                          <td className="px-4 py-4 text-slate-300">{consultant.role}</td>
                          <td className="px-4 py-4 text-slate-300">{stack.length > 0 ? stack.join(', ') : 'Sin stack parseado'}</td>
                          <td className="px-4 py-4 text-slate-400">
                            <div>{consultant.lastConversation?.happenedAt ? new Date(consultant.lastConversation.happenedAt).toLocaleDateString() : 'Sin conversaciones'}</div>
                            <div className="mt-1 max-w-md text-xs text-slate-500">{consultant.lastConversation?.summary ?? 'Captura la última conversación para enriquecer el ranking.'}</div>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`rounded-full px-2 py-1 text-xs font-medium ${freshnessClass(consultant.intelFreshness)}`}>
                              {consultant.intelFreshness ?? 'none'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </section>
        ) : null}

        {activeTab === 'conversation' ? (
          <section className="grid gap-6 rounded-3xl border border-slate-800 bg-slate-900/70 p-5 lg:grid-cols-[1.2fr_0.8fr]">
            <div>
              <h2 className="text-xl font-semibold">Registrar conversación</h2>
              <p className="mt-2 text-sm text-slate-400">Captura intel en menos de 1 minuto.</p>
              <form onSubmit={handleSaveConversation} className="mt-6 grid gap-4">
                <select 
                  className="rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3"
                  value={convForm.consultantId}
                  onChange={(e) => setConvForm({...convForm, consultantId: e.target.value})}
                  required
                >
                  <option value="">Seleccionar consultor</option>
                  {consultants.map(c => (
                    <option key={c.id} value={c.id}>{c.fullName}</option>
                  ))}
                </select>
                <select
                  className="rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3"
                  value={convForm.channel}
                  onChange={(e) => setConvForm({...convForm, channel: e.target.value})}
                >
                  <option value="whatsapp">WhatsApp</option>
                  <option value="call">Llamada</option>
                  <option value="email">Email</option>
                  <option value="meet">Meet</option>
                  <option value="other">Otro</option>
                </select>
                <textarea 
                  className="min-h-32 rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3" 
                  placeholder="¿Qué te dijo? ¿Qué quiere? ¿De qué se queja?"
                  value={convForm.summary}
                  onChange={(e) => setConvForm({...convForm, summary: e.target.value})}
                  required
                />
                <input 
                  className="rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3" 
                  placeholder="Intereses declarados (coma separada)"
                  value={convForm.declaredInterests}
                  onChange={(e) => setConvForm({...convForm, declaredInterests: e.target.value})}
                />
                <input 
                  className="rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3" 
                  placeholder="Avoids declarados (coma separada)"
                  value={convForm.declaredAvoids}
                  onChange={(e) => setConvForm({...convForm, declaredAvoids: e.target.value})}
                />
                <input 
                  className="rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3" 
                  placeholder="Disponibilidad declarada (ej: 'libre desde marzo')"
                  value={convForm.declaredAvailability}
                  onChange={(e) => setConvForm({...convForm, declaredAvailability: e.target.value})}
                />
                <button 
                  type="submit" 
                  className="rounded-2xl bg-cyan-400 px-4 py-3 font-medium text-slate-950 disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Guardando...' : 'Guardar conversación'}
                </button>
              </form>
            </div>
            <aside className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4 text-sm text-slate-400">
              <p className="font-medium text-slate-200">Objetivo de esta pantalla</p>
              <p className="mt-2">Capturar en menos de 1 minuto la señal humana que no vive en el CV.</p>
              <ul className="mt-4 space-y-2">
                <li>✓ Qué quiere ahora</li>
                <li>✓ Qué está evitando</li>
                <li>✓ Qué tan disponible se ve</li>
                <li>✓ Qué tan rápido respondería a un cliente urgente</li>
              </ul>
            </aside>
          </section>
        ) : null}

        {activeTab === 'requirement' ? (
          <section className="rounded-3xl border border-slate-800 bg-slate-900/70 p-5">
            <h2 className="text-xl font-semibold">Nuevo requerimiento</h2>
            <p className="mt-2 text-sm text-slate-400">Completa el formulario para generar un shortlist con IA.</p>
            <form onSubmit={handleCreateRequirement} className="mt-6 grid gap-4 md:grid-cols-2">
              <input 
                className="rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3" 
                placeholder="Nombre del cliente"
                value={reqForm.clientName}
                onChange={(e) => setReqForm({...reqForm, clientName: e.target.value})}
                required
              />
              <input 
                className="rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3" 
                placeholder="Stack requerido (coma separado: React, Node, AWS)"
                value={reqForm.requiredStack}
                onChange={(e) => setReqForm({...reqForm, requiredStack: e.target.value})}
                required
              />
              <select
                className="rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3"
                value={reqForm.minSeniority}
                onChange={(e) => setReqForm({...reqForm, minSeniority: e.target.value})}
              >
                <option value="junior">Junior</option>
                <option value="mid">Mid</option>
                <option value="senior">Senior</option>
                <option value="staff">Staff</option>
                <option value="principal">Principal</option>
              </select>
              <input 
                className="rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3" 
                placeholder="Rate máximo (USD/hora)"
                type="number"
                value={reqForm.rateMax}
                onChange={(e) => setReqForm({...reqForm, rateMax: e.target.value})}
              />
              <input 
                className="rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3" 
                placeholder="Fecha de inicio"
                type="date"
                value={reqForm.startDate}
                onChange={(e) => setReqForm({...reqForm, startDate: e.target.value})}
              />
              <textarea 
                className="min-h-32 rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 md:col-span-2" 
                placeholder="Texto libre del cliente - descripción del proyecto, cultura, requisitos especiales..."
                value={reqForm.freeText}
                onChange={(e) => setReqForm({...reqForm, freeText: e.target.value})}
              />
              <button 
                type="submit" 
                className="rounded-2xl bg-cyan-400 px-4 py-3 font-medium text-slate-950 md:col-span-2 disabled:opacity-50"
                disabled={shortlistLoading}
              >
                {shortlistLoading ? 'Generando shortlist con IA...' : 'Generar shortlist'}
              </button>
            </form>
          </section>
        ) : null}

        {activeTab === 'shortlists' ? (
          <section className="rounded-3xl border border-slate-800 bg-slate-900/70 p-5">
            <h2 className="text-xl font-semibold">Shortlists activos</h2>
            
            {shortlistLoading && (
              <div className="mt-6 flex items-center gap-3 text-slate-400">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-600 border-t-cyan-400"></div>
                Analizando consultores con matching semántico + generando guías con Llama 3.3...
              </div>
            )}
            
            {!shortlistResult && !shortlistLoading && (
              <div className="mt-6 rounded-2xl border border-dashed border-slate-700 p-6 text-sm text-slate-500">
                No hay shortlists activos. Ve a "Nuevo requerimiento" para generar uno.
              </div>
            )}
            
            {shortlistResult && (
              <div className="mt-6 space-y-6">
                <div className="flex flex-wrap gap-4 text-sm text-slate-400">
                  <span className="rounded-full border border-slate-700 bg-slate-950 px-3 py-1">
                    Evaluados: {shortlistResult.totalEvaluated}
                  </span>
                  <span className="rounded-full border border-slate-700 bg-slate-950 px-3 py-1">
                    Top candidatos: {shortlistResult.candidates.length}
                  </span>
                  {shortlistResult.vetoedCount > 0 && (
                    <span className="rounded-full border border-rose-800 bg-rose-950/30 px-3 py-1 text-rose-400">
                      Vetados: {shortlistResult.vetoedCount}
                    </span>
                  )}
                  {shortlistResult.staleIntelCount > 0 && (
                    <span className="rounded-full border border-amber-800 bg-amber-950/30 px-3 py-1 text-amber-400">
                      Intel stale: {shortlistResult.staleIntelCount}
                    </span>
                  )}
                  <span className="rounded-full border border-emerald-800 bg-emerald-950/30 px-3 py-1 text-emerald-400">
                    Tiempo: {shortlistResult.durationMs}ms
                  </span>
                </div>
                
                <div className="grid gap-4 lg:grid-cols-2">
                  {shortlistResult.candidates.map((candidate, index) => (
                    <div 
                      key={candidate.consultant.id} 
                      className="cursor-pointer rounded-2xl border border-slate-800 bg-slate-950/50 p-4 hover:border-cyan-800"
                      onClick={() => setSelectedCandidate(candidate)}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-cyan-400 text-xs font-bold text-slate-950">
                              {index + 1}
                            </span>
                            <h3 className="font-semibold text-slate-100">{candidate.consultant.fullName}</h3>
                          </div>
                          <p className="mt-1 text-sm text-slate-400">{candidate.consultant.role}</p>
                        </div>
                        <div className={`text-lg font-bold ${scoreColor(candidate.interviewPriority)}`}>
                          {Math.round(candidate.interviewPriority)}
                        </div>
                      </div>
                      
                      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                        <div className="rounded-lg bg-slate-900 p-2">
                          <div className="text-slate-500">Técnico</div>
                          <div className={scoreColor(candidate.scoreBreakdown.technical)}>
                            {candidate.scoreBreakdown.technical}
                          </div>
                        </div>
                        <div className="rounded-lg bg-slate-900 p-2">
                          <div className="text-slate-500">Interés</div>
                          <div className={scoreColor(candidate.scoreBreakdown.interest)}>
                            {candidate.scoreBreakdown.interest}
                          </div>
                        </div>
                        <div className="rounded-lg bg-slate-900 p-2">
                          <div className="text-slate-500">Disp.</div>
                          <div className={scoreColor(candidate.scoreBreakdown.availability)}>
                            {candidate.scoreBreakdown.availability}
                          </div>
                        </div>
                      </div>
                      
                      {candidate.risks.length > 0 && (
                        <div className="mt-3 text-xs text-rose-400">
                          ⚠ {candidate.risks.slice(0, 2).join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        ) : null}
        
        {/* Interview Guide Modal */}
        {selectedCandidate && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={() => setSelectedCandidate(null)}
          >
            <div 
              className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-slate-700 bg-slate-900 p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-xl font-semibold">Guía de entrevista: {selectedCandidate.consultant.fullName}</h3>
                <button 
                  onClick={() => setSelectedCandidate(null)}
                  className="rounded-full p-2 hover:bg-slate-800"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-6 text-sm">
                <div className="rounded-2xl border border-cyan-800/50 bg-cyan-950/20 p-4">
                  <h4 className="mb-2 font-medium text-cyan-300">Script de apertura</h4>
                  <p className="text-slate-300">{selectedCandidate.interviewGuide.openingScript}</p>
                </div>
                
                <div>
                  <h4 className="mb-2 font-medium text-slate-200">Preguntas clave</h4>
                  <ul className="space-y-2">
                    {selectedCandidate.interviewGuide.keyQuestions.map((q, i) => (
                      <li key={i} className="flex gap-2 text-slate-400">
                        <span className="text-cyan-400">{i + 1}.</span>
                        {q}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {selectedCandidate.interviewGuide.casesFromCv.length > 0 && (
                  <div>
                    <h4 className="mb-2 font-medium text-slate-200">Casos del CV para profundizar</h4>
                    {selectedCandidate.interviewGuide.casesFromCv.map((c, i) => (
                      <div key={i} className="mb-3 rounded-xl border border-slate-800 bg-slate-950 p-3">
                        <div className="font-medium text-slate-300">{c.caseTitle}</div>
                        <div className="mt-1 text-slate-500">{c.whyRelevant}</div>
                        {c.drillDownQuestions.length > 0 && (
                          <ul className="mt-2 space-y-1 text-xs text-slate-400">
                            {c.drillDownQuestions.map((q, qi) => (
                              <li key={qi}>• {q}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {selectedCandidate.interviewGuide.redFlags.length > 0 && (
                  <div className="rounded-2xl border border-rose-800/50 bg-rose-950/20 p-4">
                    <h4 className="mb-2 font-medium text-rose-300">Red Flags</h4>
                    <ul className="space-y-1">
                      {selectedCandidate.interviewGuide.redFlags.map((flag, i) => (
                        <li key={i} className="text-rose-400">⚠ {flag}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="rounded-2xl border border-emerald-800/50 bg-emerald-950/20 p-4">
                  <h4 className="mb-2 font-medium text-emerald-300">Criterio de decisión</h4>
                  <p className="text-slate-300">{selectedCandidate.interviewGuide.decisionCriteria}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
